import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../../../../components/ui/card';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../../components/ui/table";
import { Plus, Download, Upload, QrCode, Edit, Eye, FileText, Filter, X } from 'lucide-react';
import type { DashboardProject } from '../../../types/project';
import type { NDTData, PipeSize, WeldData } from '../../../types/index';
import { DialogHeader } from '../../../../../components/ui/dialog';
import { Input } from '../../../../../components/ui/input';
import { pipeSizes } from '../../../mocks/Specification';
import { Dialog, DialogContent, DialogTitle } from '../../../../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../../components/ui/select';
import { NDTData_List, WeldData_List } from '../../../mocks/production';
import { WeldDataModal } from './WeldDataModal';
import { Date as DateInput } from '../../../../../components/ui/date';
interface ProjectProductionProps {
  project: DashboardProject;
  selectedAssembly: string | null;
  setSelectedAssembly: (assembly: string | null) => void;
}
export const ProjectProduction: React.FC<ProjectProductionProps> = ({ project, selectedAssembly, setSelectedAssembly }) => {
  const [isAddingProductionItem, setIsAddingProductionItem] = useState(false);
  const [selectedPipeSize, setSelectedPipeSize] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [ndtData, setNDTData] = useState<NDTData[]>(NDTData_List);
  const [weldData, setWeldData] = useState<Record<string, WeldData[]>>(WeldData_List);
  const [selectedForgeId, setSelectedForgeId] = useState<string | null>(null);
  const [selectedPipeId, setSelectedPipeId] = useState<string | null>(null);
  const [distanceFromPipeEnd, setDistanceFromPipeEnd] = useState<number | null>(null);
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [scanningFor, setScanningFor] = useState<'forge' | 'pipe' | null>(null);
  const [isWeldDataModalOpen, setIsWeldDataModalOpen] = useState(false);
  const [selectedAssemblyForWeld, setSelectedAssemblyForWeld] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [stageFilter, setStageFilter] = useState<string>('all');

  // Add useEffect for default selection
  useEffect(() => {
    // Find first available pipe size based on project types
    const firstAvailableIndex = pipeSizes.findIndex((_, index) => {
      if (index === 0 && project.projectTypes.pipeline) return true;
      if (index === 1 && project.projectTypes.fabrication) return true;
      return false;
    });

    if (firstAvailableIndex !== -1) {
      setSelectedPipeSize(firstAvailableIndex);
    }
  }, [project.projectTypes]);

  const handleNDTUpload = async (assemblyNumber: string, file: File) => {
    const newNDTData: NDTData = {
      id: `ndt-${Date.now()}`,
      assemblyNumber,
      file,
      uploadDate: new Date(Date.now()).toISOString().split('T')[0],
      status: 'pending'
    };
    setNDTData([...ndtData, newNDTData]);
  };
  const handleOpenWeldData = (assemblyNumber: string) => {
    setSelectedAssemblyForWeld(assemblyNumber);
    setIsWeldDataModalOpen(true);
  };

  const handleCloseWeldData = () => {
    setSelectedAssemblyForWeld(null);
    setIsWeldDataModalOpen(false);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setSearchQuery('');
    setStatusFilter('all');
    setStageFilter('all');
  };

  const hasActiveFilters = searchQuery !== '' || startDate !== '' || endDate !== '' || statusFilter !== 'all' || stageFilter !== 'all';

  return (
    <div>
      <Card className="border-none shadow-none">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-semibold">Production</h3>
              <p className="text-sm text-gray-500 mt-1">Buckle Arrestor Fabrication</p>
            </div>
            <div className="flex items-center gap-2" style={{ paddingBottom: '20px' }}>
              <Button
                onClick={() => setIsAddingProductionItem(true)}
                size="sm"
                className="bg-[#e04530] text-white hover:bg-[#e04530]/90 flex items-center gap-2 min-w-[140px] justify-center"
              >
                <Plus className="h-4 w-4" />
                Add Production
              </Button>
            </div>
          </div>

          {project.projectTypes.fabrication && project.fabricationOptions?.buckleArrestor ? (
            <>
              {/* Pipe Size Selection */}
              <div className="mb-6">
                <div className="flex items-center gap-2">
                  <Label>Pipe Size:</Label>
                  <Select
                    value={selectedPipeSize.toString()}
                    onValueChange={(value) => {
                      setSelectedPipeSize(Number(value));
                      setCurrentPage(1);
                    }}
                  >
                    <SelectTrigger className="w-[400px]">
                      <SelectValue placeholder="Select pipe size" />
                    </SelectTrigger>
                    <SelectContent>
                      {pipeSizes.map((pipe, index) => {
                        // Only show pipeline option if project type is pipeline
                        if (index === 0 && project.projectTypes.pipeline) {
                          return (
                            <SelectItem key={index} value={index.toString()}>
                              Pipeline ({pipe.size} × {pipe.wallThickness})
                            </SelectItem>
                          );
                        }
                        // Only show buckle arrestor option if project type is fabrication
                        if (index === 1 && project.projectTypes.fabrication) {
                          return (
                            <SelectItem key={index} value={index.toString()}>
                              Buckle Arrestor ({pipe.size} × {pipe.wallThickness})
                            </SelectItem>
                          );
                        }
                        return null;
                      })}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Filters Section */}
              <div className="mb-6 bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">Filters</span>
                  </div>
                  {hasActiveFilters && <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="h-4 w-4 mr-1" />
                    Clear All
                  </Button>}
                </div>

                {/* Single Row Filters */}
                <div className="flex flex-wrap items-center gap-4">
                  <div className="flex-1 min-w-[150px]">
                    <Input
                      placeholder="Search assembly number..."
                      value={searchQuery}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div className="w-[200px]">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Filter by Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        <SelectItem value="not_started">Not Started</SelectItem>
                        <SelectItem value="in_progress">In Progress</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="on_hold">On Hold</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="w-[200px]">
                    <DateInput
                      type="date"
                      name="startDate"
                      className="w-full"
                      placeholder="Start date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>

                  <div className="w-[200px]">
                    <DateInput
                      type="date"
                      name="endDate"
                      className="w-full"
                      placeholder="End date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>

                  <div className="w-[200px]">
                    <Select value={stageFilter} onValueChange={setStageFilter}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Filter by Stage" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Stages</SelectItem>
                        <SelectItem value="material_prep">Material Preparation</SelectItem>
                        <SelectItem value="fitment">Fitment</SelectItem>
                        <SelectItem value="welding">Welding</SelectItem>
                        <SelectItem value="inspection">Inspection</SelectItem>
                        <SelectItem value="ndt">NDT</SelectItem>
                        <SelectItem value="coating">Coating</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>

              {/* Assembly Table */}
              <Card>
                <CardContent className="p-4">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-center">Assembly Number</TableHead>
                        <TableHead className="text-center">Drawing</TableHead>
                        <TableHead className="text-center">WPS Number</TableHead>
                        <TableHead className="text-center">Drawing Number</TableHead>
                        <TableHead className="text-center">Fitment Data</TableHead>
                        <TableHead className="text-center w-[120px]">Weld Data</TableHead>
                        <TableHead className="text-center">Visual Inspection</TableHead>
                        <TableHead className="text-center">NDT</TableHead>
                        <TableHead className="text-center">Start Date</TableHead>
                        <TableHead className="text-center">End Date</TableHead>
                        <TableHead className="text-center">Status</TableHead>
                        <TableHead className="text-center">Production Stage</TableHead>
                        <TableHead className="text-center">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedPipeSize === undefined ? (
                        <TableRow>
                          <TableCell colSpan={13} className="text-center py-4 text-gray-500">
                            Please select a pipe size to view assemblies
                          </TableCell>
                        </TableRow>
                      ) : (
                        // Map through assemblies based on selected pipe size
                        (selectedPipeSize === 0 ? ['Assy-001', 'Assy-002'] : ['Assy-003', 'Assy-004']).map((assemblyNumber) => (
                          <TableRow key={assemblyNumber}>
                            <TableCell className="text-center">{assemblyNumber}</TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center">
                                <Button
                                  variant="outline"
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                >
                                  <FileText className="h-4 w-4" />
                                  {project.documents?.length || 1}
                                </Button>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">-</TableCell>
                            <TableCell className="text-center">-</TableCell>
                            <TableCell className="text-center">-</TableCell>
                            <TableCell className="text-center">
                              {assemblyNumber === 'Assy-001' ? (
                                <div className="flex items-center justify-center">
                                  <Button
                                    variant="outline"
                                    onClick={() => handleOpenWeldData(assemblyNumber)}
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800"

                                  >
                                    <Eye className="h-4 w-4 text-gray-600" />
                                  </Button>
                                </div>
                              ) : '-'}
                            </TableCell>
                            <TableCell className="text-center">-</TableCell>
                            <TableCell className="text-center">
                              {ndtData.find(ndt => ndt.assemblyNumber === assemblyNumber) ? (
                                <Button
                                  //variant="ghost"
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                  title="View NDT Document"
                                  variant="outline"
                                >
                                  <FileText className="h-4 w-4" />
                                  {project.documents?.length || 1}
                                </Button>
                              ) :
                                (<div className="flex items-center justify-center">
                                  <input
                                    type="file"
                                    id={`ndt-upload-${assemblyNumber}`}
                                    className="hidden"
                                    accept=".pdf,.doc,.docx"
                                    title="Upload NDT Document"
                                    aria-label="Upload NDT Document"
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) handleNDTUpload(assemblyNumber, file);
                                    }}
                                  />
                                  <Button
                                    type="button"
                                    className="flex items-center gap-1 text-blue-600 hover:text-blue-800"

                                    title="Upload NDT Document"
                                    variant="outline"
                                    onClick={() => document.getElementById(`ndt-upload-${assemblyNumber}`)?.click()}
                                  >
                                    <Upload className="h-4 w-4" />
                                  </Button>
                                </div>)}
                            </TableCell>
                            <TableCell className="text-center">-</TableCell>
                            <TableCell className="text-center">-</TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center">
                                <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800">
                                  Not Started
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-center">Material Preparation</TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center">
                                <Button
                                  onClick={() => setSelectedAssembly(assemblyNumber)}
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                                  title="Edit Assembly"
                                  variant="outline"
                                >
                                  <Edit className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>

              {/* Edit Assembly Modal - Placeholder */}
              <Dialog>
                <DialogContent className="max-w-4xl">
                  <DialogHeader>
                    <DialogTitle>Edit Assembly</DialogTitle>
                  </DialogHeader>
                  <div className="grid gap-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      {/* Assembly details form will go here */}
                      <div className="space-y-2">
                        <Label>Assembly Number</Label>
                        <Input disabled value="Assy-001" />
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select>
                          <SelectTrigger>
                            <SelectValue placeholder="Select status" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_started">Not Started</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="on_hold">On Hold</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* More fields will be added here */}
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="text-center">
                <h3 className="text-lg font-medium text-gray-900">Production Not Available</h3>
                <p className="mt-2 text-sm text-gray-500">
                  Production tracking is only available for Fabrication projects with Buckle Arrestor option.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
      {/* Add the Weld Data Modal */}
      <WeldDataModal
        isOpen={isWeldDataModalOpen}
        onClose={handleCloseWeldData}
        assemblyNumber={selectedAssemblyForWeld || ''}
        welds={weldData[selectedAssemblyForWeld || ''] || []}
      />
    </div>
  );
}; 